import React, { useRef, forwardRef, useState } from "react";
import DatePickerLib from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import css from "./DatePicker.module.css";
import "./DatePicker.theme.css";
import { ru } from "date-fns/locale";

export interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  showYearPicker?: boolean;
  showMonthYearPicker?: boolean;
  showQuarterYearPicker?: boolean;
  dateFormat?: string;
  pickerType: 'year' | 'quarter' | 'month' | 'day';
  onPickerTypeChange: (type: 'year' | 'quarter' | 'month' | 'day') => void;
}

const DatePickerInput = forwardRef<HTMLInputElement, any>(({ value, onClick, placeholder, disabled, onBlur, onFocus }, ref) => (
  <div
    className={css.datePickerInput}
    onClick={disabled ? undefined : onClick}
    tabIndex={0}
    style={{ cursor: disabled ? "not-allowed" : "pointer", outline: "none", background: "transparent", border: "none", display: "flex", alignItems: "center", minHeight: "32px" }}
    ref={ref}
    onBlur={onBlur}
    onFocus={onFocus}
  >
    {value ? (
      <span className={"h2 " + css.value}>{value}</span>
    ) : (
      <span className={"p " + css.placeholder}>{placeholder}</span>
    )}
  </div>
));

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = "",
  disabled = false,
  minDate,
  showYearPicker = false,
  showMonthYearPicker = false,
  showQuarterYearPicker = false,
  dateFormat = "dd.MM.yyyy",
  pickerType,
  onPickerTypeChange
}) => {
  const datePickerRef = useRef<any>(null);

  // Determine which picker to show
  let pickerProps = {};
  let currentDateFormat = dateFormat;
  if (pickerType === 'year') {
    pickerProps = {
      showYearPicker: true,
      dateFormat: "yyyy",
      showMonthYearPicker: false,
      showQuarterYearPicker: false
    };
    currentDateFormat = "yyyy";
  } else if (pickerType === 'month') {
    pickerProps = {
      showMonthYearPicker: true,
      dateFormat: "MMMM yyyy",
      showYearPicker: false,
      showQuarterYearPicker: false
    };
    currentDateFormat = "MMMM yyyy";
  } else if (pickerType === 'quarter') {
    pickerProps = {
      showQuarterYearPicker: true,
      dateFormat: "QQQ yyyy",
      showYearPicker: false,
      showMonthYearPicker: false
    };
    currentDateFormat = "QQQ yyyy";
  } else {
    pickerProps = {
      showYearPicker: false,
      showMonthYearPicker: false,
      showQuarterYearPicker: false
    };
    currentDateFormat = dateFormat;
  }

  return (
    <div className={css.datePickerRoot}>
      <DatePickerLib
        ref={datePickerRef}
        selected={value}
        onChange={onChange}
        placeholderText={placeholder}
        disabled={disabled}
        dateFormat={currentDateFormat}
        className={css.datePickerInput}
        popperClassName={css.datePickerPopper}
        autoComplete="off"
        showPopperArrow={false}
        locale={ru}
        minDate={minDate}
        customInput={<DatePickerInput placeholder={placeholder} disabled={disabled} />}
        renderCustomHeader={() => (
          <div className={css.datePicker__types}>
            <span
              className={pickerType === 'year' ? css.activeTypeBtn : css.typeBtn}
              onClick={() => onPickerTypeChange('year')}
            >
              y
            </span>
            <span
              className={pickerType === 'quarter' ? css.activeTypeBtn : css.typeBtn}
              onClick={() => onPickerTypeChange('quarter')}
            >
              q
            </span>
            <span
              className={pickerType === 'month' ? css.activeTypeBtn : css.typeBtn}
              onClick={() => onPickerTypeChange('month')}
            >
              m
            </span>
            <span
              className={pickerType === 'day' ? css.activeTypeBtn : css.typeBtn}
              onClick={() => onPickerTypeChange('day')}
            >
              d
            </span>
          </div>
        )}
        {...pickerProps}
      />
      <span 
        className={css.datePickerIcon} 
        onClick={() => { if (datePickerRef.current && !disabled) datePickerRef.current.setOpen(true); }}
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      >
        📅
      </span>
    </div>
  );
};